import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Reset link sent if account exists');
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-teal-50 to-slate-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-slate-900">Travcations</h1>
          <p className="text-slate-500 mt-1">Reset your password</p>
        </div>

        <Card className="p-8 bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-slate-100" data-testid="forgot-password-card">
          {sent ? (
            <div className="text-center py-4" data-testid="reset-sent-msg">
              <CheckCircle className="h-12 w-12 text-teal-600 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">Check your email</h2>
              <p className="text-slate-500 text-sm mb-6">If an account exists for <strong>{email}</strong>, we've sent a password reset link.</p>
              <Link to="/login" className="text-teal-600 font-semibold hover:underline text-sm">Back to Sign In</Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-slate-900 mb-2 text-center">Forgot Password?</h2>
              <p className="text-sm text-slate-500 mb-6 text-center">Enter your email and we'll send a reset link.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-1 block">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-12 rounded-xl" placeholder="you@company.com" required
                      data-testid="input-reset-email"
                    />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 bg-gradient-to-r from-teal-600 to-emerald-600 text-white rounded-xl font-semibold" data-testid="send-reset-btn">
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
              <div className="text-center mt-4">
                <Link to="/login" className="text-sm text-slate-500 hover:text-teal-600 flex items-center justify-center gap-1">
                  <ArrowLeft className="h-3 w-3" /> Back to Sign In
                </Link>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
